"use client";
import React from "react";
import GlassIcons from './GlassIcons';
import { FiFileText, FiAlertCircle, FiCheck, FiUploadCloud, FiInfo, FiX, FiPlus, FiArrowRight, FiArrowLeft, FiHelpCircle, FiLayers } from "react-icons/fi";
import { twMerge } from "tailwind-merge";
import { motion, AnimatePresence } from "framer-motion";
import Alerta from "./Alerta";
import Loader from "./Loader";
import { startTour, stopTour } from "@ipejal/tour-manager";
import "driver.js/dist/driver.css";

const DetallePreregistro = ({ data, bitacora, bitacora_doc, reloadDetail }) => {
  const [documentos, setDocumentos] = React.useState(null);
  const [loadingDocs, setLoadingDocs] = React.useState(true);
  const [errorDocs, setErrorDocs] = React.useState(null);
  const [show, setShow] = React.useState(false);
  const [itemSeleccionado, setItemSeleccionado] = React.useState(null);
  const [alerta, setAlerta] = React.useState({ visible: false, tipo: "info", mensaje: "" });
  const [reload, setReload] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [modalStep, setModalStep] = React.useState(1);
  const apiUrl = process.env.REACT_APP_API_URL;
  const tourAttempted = React.useRef(false);
  const [motivosAceptados, setMotivosAceptados] = React.useState({});
  const seccionDocumentosRef = React.useRef(null);

  React.useEffect(() => {
    const est = data?.nombre_estado?.toUpperCase()?.trim();
    if (est === "OBSERVADO" || est === "CON OBSERVACIONES" || est === "RECHAZADO" || est === "PREREGISTRO RECHAZADO") {
      const timer = setTimeout(() => {
        if (seccionDocumentosRef.current) {
          seccionDocumentosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [data, loadingDocs]);

  React.useEffect(() => {
    setMotivosAceptados({});
  }, [itemSeleccionado]);

  React.useEffect(() => {
    return () => {
      stopTour();
    };
  }, []);

  React.useEffect(() => {
    if (!show) {
      stopTour();
    }
  }, [show]);

  const handleStartTour = (force = true) => {
    const steps = [];

    steps.push({
      target: "resubir-modal-header",
      title: "Corregir Documento",
      description: "En esta pantalla podrás corregir y volver a subir los documentos que tengan observaciones.",
      position: "bottom"
    });

    const motivoBox = document.querySelector('[data-tour="motivo-observacion-box"]');
    if (motivoBox) {
      steps.push({
        target: "motivo-observacion-box",
        title: "Motivo de Observación",
        description: "Lee con atención esta nota para saber qué debes corregir de tu documento.",
        position: "bottom"
      });
    }

    const reemplazarBtn = document.querySelector('[data-tour="reemplazar-archivo-btn"]');
    if (reemplazarBtn) {
      steps.push({
        target: "reemplazar-archivo-btn",
        title: "Reemplazar Archivo",
        description: "Presiona este botón para seleccionar y subir el archivo correcto.",
        position: "top"
      });
    }

    const anexosTab = document.querySelector('[data-tour="anexos-tab-btn"]');
    if (anexosTab) {
      steps.push({
        target: "anexos-tab-btn",
        title: "Paso 2: Anexos",
        description: "Si tu documento requiere comprobantes adicionales, podrás subirlos aquí después de corregir el principal.",
        position: "bottom"
      });
    }

    const tourJSON = {
      steps,
      options: {
        persistKey: "tour-validador-resubir",
        showProgress: true,
        allowClose: true,
        doneBtnText: "Entendido",
        nextBtnText: "Siguiente Paso",
        prevBtnText: "Volver Atrás"
      }
    };

    try {
      startTour(tourJSON, force);
    } catch (error) {
      console.error("Error al iniciar el tour en DetallePreregistro:", error);
    }
  };

  React.useEffect(() => {
    if (show && itemSeleccionado) {
      if (!tourAttempted.current) {
        tourAttempted.current = true;
        const isCompleted = localStorage.getItem("tour-validador-resubir");
        if (isCompleted !== "completed") {
          const timer = setTimeout(() => {
            handleStartTour(false);
          }, 800);
          return () => clearTimeout(timer);
        }
      }
    } else {
      tourAttempted.current = false;
    }
  }, [show, itemSeleccionado]);

  const obtenerComentarioGeneral = () => {
    const llavesDeComentario = [
      "comentario_preregistro",
      "comentario_validador",
      "comentario_rechazo",
      "comentario_observacion",
      "comentarios",
      "observaciones",
      "observacion",
      "motivo_rechazo",
      "motivo"
    ];

    // 1. Buscar en data
    if (data) {
      for (const llave of llavesDeComentario) {
        if (data[llave] && typeof data[llave] === "string" && data[llave].trim() !== "") {
          return data[llave].trim();
        }
      }
    }

    // 2. Buscar en bitacora
    if (bitacora) {
      for (const llave of llavesDeComentario) {
        if (bitacora[llave] && typeof bitacora[llave] === "string" && bitacora[llave].trim() !== "") {
          return bitacora[llave].trim();
        }
      }
    }

    // 3. Buscar en bitacora_doc
    if (Array.isArray(bitacora_doc)) {
      for (const doc of bitacora_doc) {
        for (const llave of llavesDeComentario) {
          if (doc[llave] && typeof doc[llave] === "string" && doc[llave].trim() !== "") {
            return doc[llave].trim();
          }
        }
      }
    }

    // 4. Búsqueda difusa en data
    if (data) {
      const llaveEncontrada = Object.keys(data).find(key => {
        const k = key.toLowerCase();
        return (k.includes("coment") || k.includes("observ") || k.includes("motiv") || k.includes("rechaz")) && 
               data[key] && typeof data[key] === "string" && data[key].trim() !== "";
      });
      if (llaveEncontrada) return data[llaveEncontrada].trim();
    }

    // 5. Búsqueda difusa en bitacora
    if (bitacora) {
      const llaveEncontrada = Object.keys(bitacora).find(key => {
        const k = key.toLowerCase();
        return (k.includes("coment") || k.includes("observ") || k.includes("motiv") || k.includes("rechaz")) && 
               bitacora[key] && typeof bitacora[key] === "string" && bitacora[key].trim() !== "";
      });
      if (llaveEncontrada) return bitacora[llaveEncontrada].trim();
    }

    return null;
  };

  const comentarioGeneral = obtenerComentarioGeneral();

  React.useEffect(() => {
    if (show && itemSeleccionado) {
      setModalStep(1);
      setLoading(true);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [show, itemSeleccionado]);

  const isMultiLoad = (nombre) => {
    if (!nombre) return false;
    const n = nombre.toLowerCase();
    // Keywords specified by the user + complementaria (often used)
    return ["extra", "complemento", "anexo", "adicional", "complementaria"].some(word => n.includes(word));
  };

  const handleFileChange = async (event, overrideId = null, isReplaceOverride = null) => {
    if (data?.nombre_estado === "Preregistro Rechazado") {
      setAlerta({
        visible: true,
        tipo: "error",
        mensaje: "No se pueden subir o reemplazar documentos de un registro rechazado."
      });
      event.target.value = "";
      return;
    }
    const archivo = event.target.files[0];
    if (!archivo) return;

    // Validar límite de tamaño desde .env (o 10 MB por defecto)
    const maxFileSizeMb = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 10;
    const MAX_FILE_SIZE = maxFileSizeMb * 1024 * 1024;
    if (archivo.size > MAX_FILE_SIZE) {
      setAlerta({
        visible: true,
        tipo: "warning",
        mensaje: `El archivo "${archivo.name}" supera el límite de tamaño permitido (máximo ${maxFileSizeMb} MB).`
      });
      event.target.value = ""; // Resetear input
      return;
    }

    const id_documento_subido = event.target.dataset.idDoc;
    const isMulti = overrideId ? true : (event.target.dataset.isMulti === "true");
    const isReplace = isReplaceOverride !== null ? isReplaceOverride : (event.target.dataset.isReplace === "true");

    if (isReplace) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        const filename = archivo.name;

        const ids_complementarios = itemSeleccionado?.complementariaFiles?.map(f => f.id_documento_subido) || [];

        try {
          const response = await fetch(`${apiUrl}/resubir-documento-base64?id_documento_subido=${id_documento_subido}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ base64, filename, ids_complementarios }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            setAlerta({ visible: true, tipo: "error", mensaje: `${errorData.detail || response.statusText}` });
          } else {
            setAlerta({ visible: true, tipo: "success", mensaje: "Documento reemplazado correctamente" });
            if (reloadDetail) reloadDetail();
            setReload(prev => !prev);
          }
        } catch (error) {
          setAlerta({ visible: true, tipo: "error", mensaje: "Error de conexión o inesperado" });
        }
      };
      reader.readAsDataURL(archivo);
    } else if (isMulti) {
      // Usar POST /upload-base64 para multicarga como sugirió el usuario
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        const filename = archivo.name;
        const numero = data.folio;
        const nombrePrestamo = data.nombre_corto || data.nombre || "Prestamo";
        const tipoDocumentoId = overrideId || itemSeleccionado.id_tipo_documento;
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        const codigoAfiliado = data.usuario_codigo || storedUser.username || "0";

        try {
          const esComp = overrideId ? 'true' : 'false';
          const url = `${apiUrl}/upload-base64?numero=${numero}&nombre_prestamo=${encodeURIComponent(nombrePrestamo)}&tipo_documento_id=${tipoDocumentoId}&codigo_afiliado=${codigoAfiliado}&es_complementario=${esComp}`;
          const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ base64, filename }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            setAlerta({ visible: true, tipo: "error", mensaje: `${errorData.detail || response.statusText}` });
          } else {
            setAlerta({ visible: true, tipo: "success", mensaje: "Documento agregado correctamente" });
            if (reloadDetail) reloadDetail();
            setReload(prev => !prev);
          }
        } catch (error) {
          setAlerta({ visible: true, tipo: "error", mensaje: "Error de conexión o inesperado" });
        }
      };
      reader.readAsDataURL(archivo);
    } else {
      // Mantener lógica actual para documentos normales
      const formData = new FormData();
      formData.append("file", archivo);
      formData.append("nombre_archivo_subidos", archivo.name);
      formData.append("tipo_archivo_subidos", archivo.type);
      formData.append("fecha_subida", new Date().toISOString());
      formData.append("numero", event.target.dataset.idPre);
      formData.append("usuario", 0);

      try {
        const response = await fetch(`${apiUrl}/documento-subido/${id_documento_subido}`, {
          method: "PUT",
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          setAlerta({ visible: true, tipo: "error", mensaje: `${errorData.detail || response.statusText}` });
        } else {
          setAlerta({ visible: true, tipo: "success", mensaje: "Documento actualizado correctamente" });
          if (reloadDetail) reloadDetail();
          setReload(prev => !prev);
        }
      } catch (error) {
        setAlerta({ visible: true, tipo: "error", mensaje: "Error de conexión o inesperado" });
      }
    }
  };

  const handleDeleteDocument = async (id_documento_subido) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este archivo?")) return;

    try {
      const response = await fetch(`${apiUrl}/documento-subido/${id_documento_subido}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        setAlerta({ visible: true, tipo: "error", mensaje: `${errorData.detail || response.statusText}` });
      } else {
        setAlerta({ visible: true, tipo: "success", mensaje: "Documento eliminado correctamente" });
        if (reloadDetail) reloadDetail();
        setReload(prev => !prev);
      }
    } catch (error) {
      setAlerta({ visible: true, tipo: "error", mensaje: "Error de conexión o inesperado" });
    }
  };

  const handleViewDocument = (file) => {
    const folio = file.folio || data.folio;
    const nombre_archivo = file.nombre_archivo_subidos;
    const path = file.url_archivo_subidos;
    const tipo = file.tipo_archivo;
    
    const url = `${apiUrl}/mostrar-documento/?folio=${encodeURIComponent(folio)}&nombre_archivo=${encodeURIComponent(nombre_archivo)}&path=${encodeURIComponent(path)}&tipo=${encodeURIComponent(tipo)}`;
    window.open(url, '_blank');
  };

  React.useEffect(() => {
    if (bitacora_doc) {
      setDocumentos(bitacora_doc);
      setLoadingDocs(false);
    }
  }, [bitacora_doc]);

  const handleComplementarioUpload = async (event) => {
    const archivo = event.target.files[0];
    if (data?.nombre_estado === "Preregistro Rechazado") {
      setAlerta({
        visible: true,
        tipo: "error",
        mensaje: "No se pueden subir documentos complementarios de un registro rechazado."
      });
      event.target.value = "";
      return;
    }
    if (!archivo) return;

    // Validar límite de tamaño desde .env (o 10 MB por defecto)
    const maxFileSizeMb = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 10;
    const MAX_FILE_SIZE = maxFileSizeMb * 1024 * 1024;
    if (archivo.size > MAX_FILE_SIZE) {
      setAlerta({
        visible: true,
        tipo: "warning",
        mensaje: `El archivo "${archivo.name}" supera el límite de tamaño permitido (máximo ${maxFileSizeMb} MB).`
      });
      event.target.value = ""; // Resetear input
      return;
    }

    const idRequisito = event.target.dataset.idRequisito;
    const idPreregistro = data.id_preregistro;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      const filename = archivo.name;

      try {
        const url = `${apiUrl}/upload-complementario-base64?id_preregistro=${idPreregistro}&id_requisito_documento=${idRequisito}`;
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ base64, filename }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          setAlerta({ visible: true, tipo: "error", mensaje: `${errorData.detail || response.statusText}` });
        } else {
          setAlerta({ visible: true, tipo: "success", mensaje: "Documento complementario agregado correctamente" });
          if (reloadDetail) reloadDetail();
          setReload(prev => !prev);
        }
      } catch (error) {
        setAlerta({ visible: true, tipo: "error", mensaje: "Error de conexión o inesperado" });
      }
    };
    reader.readAsDataURL(archivo);
  };

  const iconMap = {
    "Identificación oficial": <FiFileText />,
    "Identificación Oficial": <FiFileText />,
    CURP: <FiFileText />,
    "Comprobante domicilio": <FiFileText />,
    "Comprobante de Domicilio": <FiFileText />,
  };

  const items = React.useMemo(() => {
    if (!documentos) return [];
    
    const complementariaDoc = documentos.find(doc => 
      (doc.nombre_tipo_documento || doc.nombre || "").toLowerCase().includes("complementaria")
    );
    const complementariaId = complementariaDoc?.id_tipo_documento;

    const hasGlobalTrigger = documentos.some(doc => 
      isMultiLoad(doc.nombre_tipo_documento) || isMultiLoad(doc.nombre_parte)
    );

    const groups = {};
    documentos.forEach(doc => {
      const typeId = doc.id_tipo_documento;
      const nombreTipo = (doc.nombre_tipo_documento || "Documento").trim();
      
      if (!groups[typeId]) {
        groups[typeId] = {
          id_tipo_documento: typeId,
          label: nombreTipo,
          partes: {},
          // Guardar el primer id_requisito_documento encontrado para este tipo
          id_requisito_documento: doc.id_requisito_documento
        };
      }
      
      const partName = (doc.nombre_parte || "Único").trim();
      if (!groups[typeId].partes[partName]) {
        groups[typeId].partes[partName] = {
          nombre_parte: partName,
          isMulti: isMultiLoad(partName) || isMultiLoad(nombreTipo) || hasGlobalTrigger,
          files: [],
          // Datos base del primer registro de esta parte
          id_documento_subido: doc.id_documento_subido,
          id_preregistro: doc.id_preregistro,
          tipo_archivo: doc.tipo_archivo,
          comentario_validacion: doc.comentario_validacion,
          status: doc.descripcion || "Pendiente"
        };
      }
      
      if (doc.id_documento_subido && doc.url_archivo_subidos) {
        if (!doc.es_complementario && doc.id_tipo_documento !== complementariaId) {
          groups[typeId].partes[partName].files.push(doc);
        }
      }
    });

    const mappedGroups = Object.values(groups).filter(group => {
      // Ocultar Documentación Complementaria si no tiene archivos (según instrucción del usuario)
      if (group.label.toLowerCase().includes("complementaria") && 
          Object.values(group.partes).every(p => p.files.length === 0)) {
        return false;
      }
      return true;
    }).map((group) => {
      const partesArray = Object.values(group.partes);
      
      // Determinar el estatus global del grupo
      const statuses = partesArray.map(p => {
        if (p.isMulti) {
          if (p.files.some(f => ["Rechazado", "Observado", "Preregistro Rechazado"].includes(f.descripcion) || f.estatus_doc === 3)) return "Observado";
          if (p.files.length > 0 && p.files.every(f => f.descripcion === "Aprobado")) return "Aprobado";
          if (p.files.length > 0) return "En Revisión";
          return "Pendiente";
        } else {
          const s = p.files[0]?.descripcion || p.status || "Pendiente";
          return ["Rechazado", "Observado", "Preregistro Rechazado"].includes(s) ? "Observado" : s;
        }
      });

      let overallStatus = "Pendiente";
      if (statuses.some(s => s === "Observado")) {
        overallStatus = "Observado";
      } else if (statuses.every(s => s === "Aprobado")) {
        overallStatus = "Aprobado";
      } else if (statuses.some(s => !["Pendiente"].includes(s))) {
        overallStatus = "En Revisión";
      }

      return {
        ...group,
        partes: partesArray,
        icon: iconMap[group.label] || <FiFileText />,
        color: overallStatus === "Observado" ? "red" : overallStatus === "Aprobado" ? "green" : "orange",
        descripcion: overallStatus,
        parte: partesArray.length > 1 ? `${partesArray.length} Partes` : partesArray[0]?.nombre_parte,
        complementariaId: complementariaId,
        es_complementario_permitido: partesArray.some(p => p.files.some(f => f.es_complementario_permitido)),
        complementariaFiles: documentos.filter(d => 
          (d.id_tipo_documento === group.id_tipo_documento && d.es_complementario === true && d.url_archivo_subidos) ||
          (complementariaId && d.id_tipo_documento === complementariaId && d.url_archivo_subidos)
        )
      };
    });

    // Ordenar los items: Observados primero, luego En Revisión, luego Pendiente, luego Aceptado (Aprobado)
    return mappedGroups.sort((a, b) => {
      const statusOrder = {
        "observado": 1,
        "en revisión": 2,
        "en revision": 2,
        "pendiente": 3,
        "aprobado": 4
      };
      const statusA = (a.descripcion || "pendiente").toLowerCase();
      const statusB = (b.descripcion || "pendiente").toLowerCase();
      
      const orderA = statusOrder[statusA] !== undefined ? statusOrder[statusA] : 99;
      const orderB = statusOrder[statusB] !== undefined ? statusOrder[statusB] : 99;
      
      return orderA - orderB;
    });
  }, [documentos]);

  React.useEffect(() => {
    if (show && itemSeleccionado && items.length > 0) {
      const updatedItem = items.find(i => i.id_tipo_documento === itemSeleccionado.id_tipo_documento);
      if (updatedItem) {
        // Omitir el icon para poder comparar como JSON y evitar error circular de React Fiber
        const objA = { ...updatedItem, icon: null };
        const objB = { ...itemSeleccionado, icon: null };
        if (JSON.stringify(objA) !== JSON.stringify(objB)) {
          setItemSeleccionado(updatedItem);
        }
      }
    }
  }, [items]);

  if (!data) return <div className="p-10 text-center font-bold text-slate-400">No hay datos disponibles.</div>;
  if (loadingDocs) return <Loader mensaje="Cargando documentos..." />;
  if (errorDocs) return <div className="p-10 text-center text-red-600 font-bold">Error: {errorDocs}</div>;

  const statusAdmitidos = ["Rechazado", "Observado", "Preregistro Rechazado", "Con Observaciones"];
  const isObservedGroup = itemSeleccionado && ["Observado", "Rechazado"].includes(itemSeleccionado.descripcion);
  return (
    <div className="w-full flex flex-col gap-6">

      <div className="flex flex-col gap-6">
        {/* Panel Superior: Resumen */}
        <div className="w-full flex flex-col gap-6">
          <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-md flex flex-col gap-5">
            <header className="flex items-center gap-4 border-b border-slate-100 pb-4">
              <div className="size-11 bg-primary/10 rounded-2xl flex-shrink-0 flex items-center justify-center text-primary">
                <FiLayers className="size-6" />
              </div>
              <div className="min-w-0">
                <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-wider">Folio</h4>
                <p className="text-2xl sm:text-3xl font-black text-slate-900 leading-none">{data.folio}</p>
              </div>
            </header>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100 shadow-xs">
                <span className="block text-[10px] sm:text-xs font-extrabold text-slate-400 uppercase mb-2 tracking-wide">Estado Preregistro</span>
                <div className="flex items-center gap-2">
                  <span className={twMerge(
                    "size-2.5 rounded-full flex-shrink-0",
                    data.nombre_estado === "Aprobado" ? "bg-green-600" : 
                    (data.nombre_estado === "Rechazado" || data.nombre_estado === "Observado") ? "bg-red-600" : "bg-amber-500"
                  )}></span>
                  <span className="text-xs sm:text-sm font-black uppercase text-slate-800 leading-tight">
                    {data.nombre_estado}
                  </span>
                </div>
              </div>
              <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100 shadow-xs">
                <span className="block text-[10px] sm:text-xs font-extrabold text-slate-400 uppercase mb-2 tracking-wide">Fecha Registro</span>
                <span className="text-xs sm:text-sm font-black text-slate-800 block">
                  {new Date(data.fecha_preregistro).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>

            {comentarioGeneral && (
              <div data-tour="observacion-general-box" className="bg-slate-50 border border-slate-200 rounded-3xl p-5 space-y-4 shadow-sm relative overflow-hidden">
                <header className="flex items-center gap-3 relative z-10">
                  <div className="size-8 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0">
                    <FiAlertCircle className="size-4.5" />
                  </div>
                  <div>
                    <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none">Estatus: Observado</h5>
                    <h4 className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight mt-1">Detalle de la Observación</h4>
                  </div>
                </header>

                <div className="bg-white border border-slate-100 p-4 rounded-2xl shadow-xs relative z-10">
                  <p className="text-slate-800 text-xs sm:text-sm font-medium leading-relaxed">
                    "{comentarioGeneral}"
                  </p>
                </div>

                {["Rechazado", "Preregistro Rechazado", "Observado"].includes(data.nombre_estado) && (
                  <div className="bg-slate-100/60 border border-slate-200/50 p-4 rounded-2xl flex items-start gap-3 relative z-10">
                    <span className="text-base leading-none mt-0.5">💡</span>
                    <p className="text-xs text-slate-600 font-medium leading-relaxed">
                      <strong>Próximo Paso:</strong> Para corregir este trámite, pulse el botón <span className="text-primary font-bold uppercase tracking-wider text-[11px]">"Iniciar Trámite"</span> en la pantalla anterior y suba la documentación correcta.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Panel Inferior: Lista */}
        <div ref={seccionDocumentosRef} className="w-full">
          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200/80 shadow-lg shadow-slate-100/50 flex flex-col gap-8">
            <header className="border-b border-slate-100 pb-6 text-center sm:text-left">
              <h4 className="text-xs font-bold uppercase text-primary mb-1 tracking-wider">Expediente Digital</h4>
              <p className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Estatus de Documentación</p>
              <div className="flex flex-col sm:flex-row items-center gap-3 mt-4">
                <p className="text-xs sm:text-sm text-slate-500 font-medium">Selecciona un documento para ver su detalle o subir archivos</p>
                {(documentos || []).some(d => ["Observado", "Rechazado", "Preregistro Rechazado"].includes(d.nombre)) && data?.nombre_estado !== "Preregistro Rechazado" && (
                  <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-bold px-3.5 py-1.5 rounded-xl shadow-xs">
                    <FiAlertCircle className="size-4 text-amber-400 shrink-0" />
                    <span>Pulsa en los documentos observados para corregir</span>
                  </div>
                )}
              </div>
            </header>

            <div className="flex-1 flex items-center justify-center">
              <GlassIcons
                items={items}
                show={show}
                setShow={setShow}
                setItemSeleccionado={setItemSeleccionado}
                data={data}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal Detalle */}
      <AnimatePresence>
        {show && itemSeleccionado && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="w-full max-w-2xl bg-white rounded-[2rem] shadow-2xl overflow-hidden"
            >
              <div data-tour="resubir-modal-header" className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-4">
                  <div className="size-14 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center text-2xl border border-slate-200/80 shadow-xs">
                    {itemSeleccionado.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight leading-snug">{itemSeleccionado.label}</h2>
                    <span className="inline-block mt-1 text-xs font-semibold text-slate-500">Sección: {itemSeleccionado.parte}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleStartTour}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl text-slate-700 hover:text-primary font-bold text-xs shadow-xs active:scale-95 transition-all"
                  >
                    <FiHelpCircle className="size-4" />
                    <span>Guía</span>
                  </button>
                  <button 
                    onClick={() => {
                      stopTour();
                      setShow(false);
                    }} 
                    className="size-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors shadow-xs"
                  >
                    <FiX className="size-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 sm:p-10 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {(() => {
                  const hasAnexos = itemSeleccionado.complementariaId || itemSeleccionado.es_complementario_permitido;
                  const hasMainFileLoaded = itemSeleccionado.partes.some(p => p.files.length > 0);
                  const isObservedGroup = itemSeleccionado && ["Observado", "Rechazado"].includes(itemSeleccionado.descripcion);
                  const step1Completed = hasMainFileLoaded && !isObservedGroup;

                  return (
                    <>
                      {hasAnexos && (
                        <div className="flex flex-col sm:flex-row items-center gap-2 mb-2">
                          <button 
                            onClick={() => setModalStep(1)}
                            className={twMerge(
                              "w-full sm:flex-1 py-3 px-4 rounded-xl font-bold text-xs sm:text-sm transition-all border",
                              modalStep === 1 
                                ? "bg-primary text-white border-primary shadow-sm" 
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                            )}
                          >
                            Paso 1: Documento Principal
                          </button>
                          <button 
                            onClick={() => {
                              if (step1Completed) setModalStep(2);
                            }}
                            data-tour="anexos-tab-btn"
                            className={twMerge(
                              "w-full sm:flex-1 py-3 px-4 rounded-xl font-bold text-xs sm:text-sm transition-all border flex items-center justify-center gap-2",
                              modalStep === 2 
                                ? "bg-purple-600 text-white border-purple-600 shadow-sm" 
                                : step1Completed 
                                  ? "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 cursor-pointer" 
                                  : "bg-slate-100 text-slate-400 border-slate-200 opacity-60 cursor-not-allowed"
                            )}
                            disabled={!step1Completed}
                          >
                            Paso 2: Anexos {!step1Completed && "(Bloqueado)"}
                          </button>
                        </div>
                      )}

                      {/* PASO 1: Documento Principal */}
                      {(modalStep === 1 || !hasAnexos) && (
                        <div className="space-y-6">
                          {itemSeleccionado.partes.map((part, pIdx) => {
                            const partStatus = part.isMulti 
                              ? (part.files.some(f => ["Rechazado", "Observado", "Preregistro Rechazado"].includes(f.descripcion) || f.estatus_doc === 3) ? "Observado" : (part.files.length > 0 && part.files.every(f => f.descripcion === "Aprobado") ? "Aprobado" : (part.files.length > 0 ? "En Revisión" : "Pendiente")))
                              : (part.files[0]?.descripcion || part.status || "Pendiente");
                            
                            const isPartObserved = ["Rechazado", "Observado", "Preregistro Rechazado"].includes(partStatus);
                            const mainFile = part.files.find(f => f.es_complementario === false) || part.files[0];

                            return (
                              <div key={pIdx} className="bg-slate-50/70 rounded-3xl p-6 border border-slate-200 space-y-4">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                  <div className="flex items-center gap-3">
                                    <div className="size-9 bg-white rounded-xl flex items-center justify-center text-slate-700 shadow-xs border border-slate-200">
                                      <FiFileText className="size-4.5" />
                                    </div>
                                    <div>
                                      <h3 className="text-sm font-bold text-slate-800">{part.nombre_parte}</h3>
                                      {part.isMulti && <span className="text-xs font-medium text-primary">Múltiples archivos permitidos</span>}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={twMerge(
                                      "px-3 py-1 rounded-xl text-xs font-bold text-white shadow-xs",
                                      isPartObserved ? "bg-red-600" : 
                                      partStatus === 'Aprobado' ? "bg-emerald-600" : "bg-amber-500"
                                    )}>
                                      {partStatus === "Rechazado" ? "Observado" : partStatus}
                                    </span>
                                  </div>
                                </div>

                                {/* Lógica de Lectura y Aceptación de la Observación */}
                                {(() => {
                                  const partKey = part.id_documento_subido || part.nombre_parte || pIdx;
                                  const haAceptadoMotivo = !!motivosAceptados[partKey];
                                  const comentarioMotivo = part.files.find(f => f.comentario_validacion)?.comentario_validacion || part.comentario_validacion || "Sin comentarios.";

                                  return (
                                    <>
                                      {isPartObserved && !haAceptadoMotivo ? (
                                        <div className="space-y-4">
                                          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                                            <header className="flex items-center gap-2 border-b border-slate-200 pb-3">
                                              <FiAlertCircle className="size-5 text-amber-500" />
                                              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Motivo de Observación</h4>
                                            </header>

                                            <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-xs">
                                              <p className="text-slate-800 text-sm sm:text-base font-semibold leading-relaxed">
                                                "{comentarioMotivo}"
                                              </p>
                                            </div>
                                          </div>

                                          <button
                                            onClick={() => setMotivosAceptados(prev => ({ ...prev, [partKey]: true }))}
                                            className="w-full py-3.5 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary/90 transition-all shadow-sm active:scale-95 flex items-center justify-center gap-2"
                                          >
                                            <span>Entendido, subir documentos</span>
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          {/* Lógica de Archivos para esta Parte */}
                                          {part.isMulti ? (
                                            <div className="space-y-4">
                                              {(part.files.length === 0 || isPartObserved) && data?.nombre_estado !== "Preregistro Rechazado" && (
                                                <label 
                                                  data-tour="reemplazar-archivo-btn"
                                                  className="flex items-center justify-center gap-2.5 w-full py-3.5 bg-white text-slate-700 hover:text-primary font-bold text-sm rounded-xl border border-dashed border-slate-300 hover:border-primary transition-all cursor-pointer shadow-xs"
                                                >
                                                  <FiPlus className="size-4" />
                                                  <span>Agregar otro archivo</span>
                                                  <input
                                                    type="file"
                                                    className="hidden"
                                                    onChange={handleFileChange}
                                                    data-id-pre={data.id_preregistro}
                                                    data-is-multi="true"
                                                  />
                                                </label>
                                              )}

                                              {part.files.length > 0 ? (
                                                <div className="space-y-2">
                                                  {part.files.map((file, fIdx) => (
                                                    <div key={file.id_documento_subido || fIdx} className="flex items-center justify-between bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs">
                                                      <div className="flex items-center gap-3 min-w-0">
                                                        <div className="size-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600 flex-shrink-0">
                                                          <FiFileText className="size-4" />
                                                        </div>
                                                        <div className="min-w-0">
                                                          <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">Documento Adjunto {fIdx + 1}</p>
                                                          <span className={twMerge(
                                                            "inline-block text-[11px] font-bold px-2 py-0.5 rounded-lg mt-0.5",
                                                            file.descripcion === "Aprobado" ? "bg-emerald-100 text-emerald-800" :
                                                            ["Rechazado", "Observado"].includes(file.descripcion) ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                                                          )}>
                                                            {file.descripcion === "Rechazado" ? "Observado" : file.descripcion}
                                                          </span>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-2">
                                                        <button 
                                                          onClick={() => handleViewDocument(file)}
                                                          className="size-8 rounded-lg bg-white border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-primary hover:text-white transition-all shadow-xs"
                                                          title="Ver documento"
                                                        >
                                                          <FiInfo className="size-4" />
                                                        </button>
                                                        {(file.estatus_doc === 3 || ["Rechazado", "Observado", "Preregistro Rechazado"].includes(file.descripcion)) && data?.nombre_estado !== "Preregistro Rechazado" && (
                                                          <label 
                                                            data-tour="reemplazar-archivo-btn"
                                                            className="size-8 rounded-lg bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-all cursor-pointer m-0 shadow-xs" 
                                                            title="Resubir documento rechazado"
                                                          >
                                                            <FiUploadCloud className="size-4" />
                                                            <input
                                                              type="file"
                                                              className="hidden"
                                                              data-id-doc={file.id_documento_subido}
                                                              onChange={(e) => handleFileChange(e, null, true)}
                                                            />
                                                          </label>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              ) : (
                                                <p className="text-xs text-slate-400 italic text-center py-2">Sin archivos cargados.</p>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="space-y-4">
                                              {part.files.length > 0 && mainFile ? (
                                                <div className="flex items-center justify-between bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200 shadow-xs">
                                                  <div className="flex items-center gap-3 min-w-0">
                                                    <div className="size-8 bg-slate-100 text-slate-700 rounded-lg flex items-center justify-center border border-slate-200 flex-shrink-0">
                                                      <FiCheck className="size-4 text-emerald-600" />
                                                    </div>
                                                    <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">Documento Adjunto</p>
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    <button 
                                                      onClick={() => handleViewDocument(mainFile)}
                                                      className="size-8 rounded-lg bg-white border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-primary hover:text-white transition-all shadow-xs"
                                                      title="Ver documento"
                                                    >
                                                      <FiInfo className="size-4" />
                                                    </button>
                                                  </div>
                                                </div>
                                              ) : (
                                                data?.nombre_estado !== "Preregistro Rechazado" ? (
                                                  <label 
                                                    data-tour="reemplazar-archivo-btn"
                                                    className="flex items-center justify-center gap-2.5 w-full py-4 bg-white text-slate-700 hover:text-primary font-bold text-sm rounded-xl border border-dashed border-slate-300 hover:border-primary transition-all cursor-pointer shadow-xs"
                                                  >
                                                    <FiUploadCloud className="size-5" />
                                                    <span>Subir Documento</span>
                                                    <input
                                                      type="file"
                                                      className="hidden"
                                                      onChange={handleFileChange}
                                                      data-id-doc={part.id_documento_subido}
                                                      data-id-pre={data.id_preregistro}
                                                      data-is-multi="false"
                                                    />
                                                  </label>
                                                ) : (
                                                  <div className="flex items-center justify-center w-full py-4 bg-slate-100 text-slate-500 font-bold text-xs rounded-xl border border-slate-200">
                                                    Sin Archivo Cargado
                                                  </div>
                                                )
                                              )}

                                              {(isPartObserved || (mainFile && mainFile.estatus_doc === 3)) && part.files.length > 0 && mainFile && data?.nombre_estado !== "Preregistro Rechazado" && (
                                                <label 
                                                  data-tour="reemplazar-archivo-btn"
                                                  className="flex items-center justify-center gap-2.5 w-full py-3.5 px-6 bg-primary text-white font-bold text-sm rounded-xl shadow-sm hover:bg-primary/90 transition-all cursor-pointer active:scale-95"
                                                >
                                                  <FiUploadCloud className="size-5 shrink-0" />
                                                  <span>Reemplazar Archivo</span>
                                                  <input
                                                    type="file"
                                                    className="hidden"
                                                    data-id-doc={mainFile.id_documento_subido}
                                                    data-id-pre={data.id_preregistro}
                                                    data-is-multi="false"
                                                    onChange={(e) => handleFileChange(e, null, true)}
                                                  />
                                                </label>
                                              )}
                                            </div>
                                          )}

                                          {/* Observación inferior persistente como recordatorio */}
                                          {isPartObserved && (
                                            <div 
                                              data-tour="motivo-observacion-box"
                                              className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-2 mt-4"
                                            >
                                              <h4 className="text-xs font-bold text-slate-700 flex items-center gap-2">
                                                <FiAlertCircle className="size-4 text-amber-500 shrink-0" /> Motivo de la Observación:
                                              </h4>
                                              <div className="bg-white border border-slate-200 p-3.5 rounded-xl shadow-xs">
                                                <p className="text-slate-800 text-sm sm:text-base font-semibold leading-relaxed">
                                                  "{comentarioMotivo}"
                                                </p>
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            );
                          })}

                          {hasAnexos && (
                            <div className="pt-2">
                              {step1Completed ? (
                                <button 
                                  onClick={() => setModalStep(2)}
                                  className="w-full py-4 bg-purple-600 text-white font-black text-[11px] uppercase rounded-xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                                >
                                  Continuar a Anexos y Complementos <FiArrowRight className="size-4" />
                                </button>
                              ) : (
                                <div className="w-full py-4 bg-slate-100 text-slate-400 font-black text-[10px] uppercase rounded-xl border border-slate-200 shadow-sm flex items-center justify-center gap-2 text-center">
                                  <FiInfo className="size-4 text-amber-500" />
                                  {hasMainFileLoaded 
                                    ? "Reemplace primero el archivo principal para continuar a anexos" 
                                    : "Suba primero el archivo principal para continuar a anexos"}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* PASO 2: Documentación Complementaria */}
                      {modalStep === 2 && hasAnexos && (
                        <div className="space-y-6">
                          <div className="bg-slate-50/70 rounded-3xl p-6 border border-slate-200 space-y-6">
                            <header className="flex items-center gap-3">
                              <div className="size-10 bg-purple-100 text-purple-700 rounded-xl flex items-center justify-center">
                                <FiPlus className="size-5" />
                              </div>
                              <div>
                                <h4 className="text-sm font-bold text-slate-800">Anexos y Complementos</h4>
                                <p className="text-xs font-medium text-slate-500">Documentación adicional del expediente</p>
                              </div>
                            </header>

                            <div className="space-y-4">
                              {data?.nombre_estado !== "Preregistro Rechazado" ? (
                                <label className="flex items-center justify-center gap-2.5 w-full py-3.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm rounded-xl shadow-sm transition-all cursor-pointer active:scale-95">
                                  <FiPlus className="size-4.5" />
                                  <span>Agregar Documentación Complementaria</span>
                                  <input
                                    type="file"
                                    className="hidden"
                                    data-id-requisito={itemSeleccionado.id_requisito_documento}
                                    onChange={handleComplementarioUpload}
                                  />
                                </label>
                              ) : null}

                              {itemSeleccionado.complementariaFiles && itemSeleccionado.complementariaFiles.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {itemSeleccionado.complementariaFiles.map((file, fIdx) => (
                                    <div key={file.id_documento_subido || fIdx} className="flex items-center justify-between bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs">
                                      <div className="flex items-center gap-3 min-w-0">
                                        <FiFileText className="size-4.5 text-purple-600 flex-shrink-0" />
                                        <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">Documento Anexo {fIdx + 1}</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button 
                                          onClick={() => handleViewDocument(file)}
                                          className="size-8 rounded-lg bg-white border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-purple-600 hover:text-white transition-all shadow-xs"
                                          title="Ver anexo"
                                        >
                                          <FiInfo className="size-4" />
                                        </button>
                                        {(file.estatus_doc === 3 || ["Rechazado", "Observado", "Preregistro Rechazado"].includes(file.descripcion)) && data?.nombre_estado !== "Preregistro Rechazado" && (
                                          <label className="size-8 rounded-lg bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-all shadow-xs cursor-pointer m-0" title="Resubir documento rechazado">
                                            <FiUploadCloud className="size-4" />
                                            <input
                                              type="file"
                                              className="hidden"
                                              data-id-doc={file.id_documento_subido}
                                              onChange={(e) => handleFileChange(e, null, true)}
                                            />
                                          </label>
                                        )}

                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-6 bg-white rounded-2xl border border-dashed border-slate-200">
                                  <p className="text-xs text-slate-400 font-semibold">Sin anexos complementarios</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <button 
                            onClick={() => setModalStep(1)}
                            className="w-full py-3.5 bg-white border border-slate-300 text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 shadow-xs active:scale-95"
                          >
                            <FiArrowLeft className="size-4" /> Volver al Documento Principal
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {alerta.visible && (
        <Alerta
          tipo={alerta.tipo}
          mensaje={alerta.mensaje}
          onClose={() => setAlerta({ ...alerta, visible: false })}
        />
      )}
    </div>
  );
};

export default DetallePreregistro;
